import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { fmtBytes } from "@/lib/api";
import { AlertTriangle, ChevronsUpDown } from "lucide-react";

const SYSTEM = ["information_schema", "performance_schema", "mysql", "sys"];

export default function ObjectTreeSelector({ report, scope, onChange }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const dbs = report?.databases || [];
  const selectable = dbs.filter((d) => !d.is_system || scope.include_system);
  const filtered = selectable.filter((d) => !filter || d.name.toLowerCase().includes(filter.toLowerCase()));

  const selection = scope.databases || {};

  const setSelection = (next) => onChange({ ...scope, databases: next });

  const toggleDb = (db, checked) => {
    const next = { ...selection };
    if (checked) next[db.name] = { all: true, tables: [] };
    else delete next[db.name];
    setSelection(next);
  };

  const toggleTable = (db, table, checked) => {
    const next = { ...selection };
    const cur = next[db.name] || { all: false, tables: [] };
    let tables = cur.all ? db.tables.map((t) => t.name) : [...(cur.tables || [])];
    if (checked) tables = [...new Set([...tables, table])];
    else tables = tables.filter((t) => t !== table);
    if (tables.length === 0) delete next[db.name];
    else next[db.name] = { all: tables.length === db.tables.length, tables };
    setSelection(next);
  };

  const isDbSelected = (db) => !!selection[db.name];
  const isTableSelected = (db, table) => {
    const cur = selection[db.name];
    if (!cur) return false;
    return cur.all || (cur.tables || []).includes(table);
  };

  const selectAll = () => {
    const next = {};
    selectable.forEach((d) => { next[d.name] = { all: true, tables: [] }; });
    setSelection(next);
  };
  const deselectAll = () => setSelection({});

  return (
    <div className="grid-panel" data-testid="object-tree-selector">
      <div className="px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" className="rounded-none h-8 text-xs uppercase" onClick={selectAll} data-testid="tree-select-all">{t("objectTree.selectAll")}</Button>
          <Button type="button" size="sm" variant="outline" className="rounded-none h-8 text-xs uppercase" onClick={deselectAll} data-testid="tree-deselect-all">{t("objectTree.deselectAll")}</Button>
        </div>
        <Input placeholder={t("objectTree.search")} value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-none h-8 max-w-[200px]" data-testid="tree-search" />
      </div>

      <div className="p-3 max-h-[360px] overflow-y-auto">
        {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">{t("objectTree.noDbs")}</p>}
        <Accordion type="multiple" className="space-y-1">
          {filtered.map((d) => (
            <AccordionItem key={d.name} value={d.name} className="border border-border" data-testid={`tree-db-${d.name}`}>
              <div className="flex items-center px-3 py-2 gap-3">
                <Checkbox checked={isDbSelected(d)} onCheckedChange={(c) => toggleDb(d, c)} data-testid={`tree-db-checkbox-${d.name}`} />
                <AccordionTrigger className="flex-1 py-0 hover:no-underline">
                  <div className="flex items-center gap-2 text-left">
                    <span className="font-mono font-semibold">{d.name}</span>
                    {d.is_system && <span className="font-mono text-[10px] uppercase bg-yellow-100 text-yellow-800 px-1.5">{t("objectTree.system")}</span>}
                    <span className="font-mono text-xs text-muted-foreground">{t("objectTree.tables", { count: d.tables.length })}</span>
                  </div>
                </AccordionTrigger>
              </div>
              <AccordionContent className="px-3 pb-3">
                <div className="tree-line ml-3 pl-4 space-y-1.5">
                  {d.tables.map((t) => {
                    const warn = t.engine && t.engine.toLowerCase() !== "innodb";
                    return (
                      <div key={t.name} className="flex items-center gap-2.5" data-testid={`tree-table-${d.name}-${t.name}`}>
                        <Checkbox checked={isTableSelected(d, t.name)} onCheckedChange={(c) => toggleTable(d, t.name, c)} />
                        <span className="font-mono text-xs">{t.name}</span>
                        <span className="font-mono text-[10px] text-muted-foreground">{t.engine || "?"} · {fmtBytes(t.size)}</span>
                        {warn && <span className="flex items-center gap-1 text-[10px] text-yellow-700"><AlertTriangle className="h-3 w-3" />non-InnoDB</span>}
                      </div>
                    );
                  })}
                  {d.tables.length === 0 && <p className="font-mono text-xs text-muted-foreground">{t("objectTree.noTables")}</p>}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
      <div className="px-4 py-2 border-t border-border font-mono text-xs text-muted-foreground flex items-center gap-2">
        <ChevronsUpDown className="h-3 w-3" /> {t("objectTree.selected", { count: Object.keys(selection).length })}
      </div>
    </div>
  );
}
